<template>
  <ContentSection class="-my-16">
    <Container indent="wide">
      <div
        class="bg-gradient-to-tr from-blue-50 to-blue-10/40 px-4 py-7 after:bg-gradient-to-tr sm:px-14 sm:py-16"
      >
        <!-- TITLE -->
        <h2
          v-if="data.heading"
          class="default-component-title"
          :class="data.text ? 'mb-3 sm:mb-5' : 'mb-8 sm:mb-10'"
          v-html="$replaceHtmlTags(data.heading)"
        />
        <div
          v-if="data.text"
          class="mb-8 max-w-text text-base sm:mb-10 sm:text-md"
          v-html="data.text"
        />

        <!-- POLL -->
        <ul
          class="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2 xl:grid-cols-4"
        >
          <li v-for="answer in data.answers" :key="answer.id" class="flex">
            <button
              class="group flex w-full gap-[1.125rem] rounded-sm border-2 bg-white p-[1.125rem] transition-colors md:flex-col md:items-center md:border-[0.1875rem] md:p-8"
              :class="
                selectedAnswer?.id === answer.id
                  ? 'border-blue-800'
                  : 'border-transparent'
              "
              :disabled="state === 'success'"
              :aria-label="answer.title"
              @click="selectedAnswer = answer"
            >
              <!-- Image -->
              <div class="mt-1 h-10 w-auto shrink-0 md:mt-4 md:h-16">
                <AppImage
                  v-if="answer.icon"
                  class="h-10 w-auto transition-transform ease-out md:h-16"
                  :class="{
                    'group-hover:scale-110': state !== 'success',
                    'scale-110': selectedAnswer?.id === answer.id,
                  }"
                  loading="lazy"
                  :src="answer.icon"
                  :alt="
                    $i18n.locale === 'fr' ? answer.icon.alt_fr : answer.icon.alt
                  "
                  style="
                    filter: invert(48%) sepia(59%) saturate(5684%)
                      hue-rotate(174deg) brightness(96%) contrast(101%);
                  "
                />
              </div>
              <div>
                <!-- Title -->
                <span
                  class="block w-full text-left text-lg font-medium md:mb-2 md:mt-7 md:text-center"
                >
                  {{ answer.title }}
                </span>
                <!-- Text -->
                <span class="block w-full text-left md:text-center">{{
                  answer.text
                }}</span>
              </div>
            </button>
          </li>
        </ul>

        <div class="mt-12 min-h-[2.625rem] sm:min-h-[3.125rem]">
          <Transition name="fade" mode="out-in">
            <p
              v-if="state === 'success'"
              key="0"
              class="text-medium max-w-text text-center text-blue-800 sm:text-left sm:text-md"
            >
              {{ data.feedback_success_text }}
            </p>
            <div v-else key="1">
              <AppButton
                class=""
                :disabled="!selectedAnswer"
                :is-loading="isLoading"
                @click="submitFeedback()"
              >
                {{ data.feedback_send_button_text }}
              </AppButton>
            </div>
          </Transition>
        </div>
        <p
          v-if="state === 'error'"
          key="0"
          class="text-medium mt-10 max-w-text text-red sm:text-md"
        >
          {{ data.feedback_success_text }}
        </p>
      </div>
    </Container>
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, required: true },
})

const config = useRuntimeConfig()

const pageId = inject('pageId', null)

const selectedAnswer = ref(null)
const isLoading = ref(false)
const state = ref('idle')

async function submitFeedback() {
  if (!selectedAnswer.value) {
    return
  }

  isLoading.value = true

  const payload = {
    component_id: props.data.id,
    answer_id: selectedAnswer.value.id,
  }

  try {
    await api(`/opinion-poll/${pageId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Origin: config.public.APP_URL,
      },
      body: JSON.stringify(payload),
    })

    state.value = 'success'
  } catch (error) {
    state.value = 'error'
  } finally {
    isLoading.value = false
  }
}
</script>
